import './RadialAscendency.css';
import { useCallback, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UncontrolledReactSVGPanZoom } from 'react-svg-pan-zoom';
import { arc, pie, type PieArcDatum } from 'd3-shape';
import { schemeCategory10 } from 'd3-scale-chromatic';
import { Alert, Box, CircularProgress, Slider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import {
    familyExtended,
    fetchAncestorFamilies,
    fetchAncestorPersons,
    fetchEventsByHandles,
    fetchPersonByGrampsId,
    handlesDigest,
    personExtended,
    restEventDateString,
    restPersonName,
    type RestEvent,
    type RestFamily,
    type RestPerson,
} from '../api/grampsRest';
import { readBearerToken, subscribeToBearerToken } from '../grampsAuth';
import { PersonContextMenu } from '../components/PersonContextMenu';
import { RestPersonFilter, RestPersonFilterActiveSummary } from '../components/RestPersonFilter';
import { useElementSize } from '../hooks/useElementSize';
import { useViewRoute } from '../viewRoute';

const ASPECT_RATIO = 9 / 16;
// Truncate() caps names at this length; font sizing assumes the worst case
// (a full-length name) so a ring's text never overflows into its neighbours.
const MAX_NAME_CHARS = 16;
// Rough average glyph width as a fraction of font-size for the theme's font,
// used to bound font-size by the chord (horizontal) space an arc offers.
const CHAR_WIDTH_FACTOR = 0.62;
const MIN_FONT_PX = 4;
const MAX_FONT_PX = 32;
type AncestorSlot = RestPerson | undefined;

// Real family data rarely hits the worst-case 16-char name on every slot at
// once, so deeper rings (many slots, each individually low-risk) can safely
// be rendered more generously than the raw safe bound below -- ring 4 gets a
// modest boost, rings past it (which the raw bound shrinks fastest) get more.
function depthBoost(depth: number): number {
    if (depth === 4) return 1.5;
    if (depth === 5) return 2;
    if (depth >= 6) return 4;
    return 1;
}

// Computes, per ring depth, the largest font-size that is guaranteed to fit
// both the ring's radial thickness (so two stacked name lines don't overlap
// the next ring) and the chord width of its narrowest slot at that depth (so
// text doesn't overlap neighbouring slots) -- replacing the old viewport-width
// based CSS breakpoints, which knew nothing about the actual container size,
// the selected generation count, or how many slots a ring was split into.
function computeFontSizes(ringWidth: number, level: number, fontScale: number): number[] {
    if (!ringWidth) return [];
    const rawSizes = [...Array(level)].map((_, depth) => {
        const slotCount = 2 ** depth;
        const twoLine = depth < 8;
        const midRadius = (depth + 0.5) * ringWidth;
        const outerRadius = (depth + 1) * ringWidth;
        // A lone slot (depth 0, the root) has no neighbour to collide with --
        // its only real bound is its ring's own outer-circle curvature, not
        // the neighbour-chord formula below (which degenerates for a full
        // half-circle slot and would make the root needlessly tiny).
        const chordWidth = slotCount === 1
            ? 2 * Math.sqrt(outerRadius ** 2 - midRadius ** 2)
            : 2 * midRadius * Math.sin(Math.PI / slotCount / 2);
        const angularLimit = chordWidth / (MAX_NAME_CHARS * CHAR_WIDTH_FACTOR);
        const radialLimit = ringWidth / (twoLine ? 2.4 : 1.3);
        return Math.min(angularLimit, radialLimit) * depthBoost(depth);
    });
    // Enforce a visual hierarchy on top of the safe bounds: an ancestor ring
    // should never render larger than the ring of the person it descends
    // from, however generous that ring's own raw bound happens to be.
    let previous = Infinity;
    return rawSizes.map((raw) => {
        previous = Math.min(previous, raw);
        return Math.min(MAX_FONT_PX, Math.max(MIN_FONT_PX, previous * fontScale));
    });
}

function parentScore(family: RestFamily, childHandle: string | undefined): number {
    const ref = (family.child_ref_list ?? []).find((child) => child.ref === childHandle);
    return ((ref?.frel ?? '').toLowerCase() === 'birth' ? 1 : 0) + ((ref?.mrel ?? '').toLowerCase() === 'birth' ? 1 : 0);
}

function indexFamiliesByChild(families: RestFamily[]): Map<string, RestFamily[]> {
    const result = new Map<string, RestFamily[]>();
    families.forEach((family) => (family.child_ref_list ?? []).forEach((child) => {
        if (!child.ref) return;
        const matches = result.get(child.ref) ?? [];
        matches.push(family);
        result.set(child.ref, matches);
    }));
    return result;
}

function bestParentFamily(person: RestPerson, familiesByChild: Map<string, RestFamily[]>): RestFamily | undefined {
    return (familiesByChild.get(person.handle ?? '') ?? []).reduce<RestFamily | undefined>(
        (best, family) => !best || parentScore(family, person.handle) > parentScore(best, person.handle) ? family : best,
        undefined,
    );
}

function buildSlots(root: RestPerson | undefined, people: RestPerson[], families: RestFamily[], levels: number): AncestorSlot[][] {
    const peopleByHandle = new Map(people.filter((person) => person.handle).map((person) => [person.handle!, person]));
    const familiesByChild = indexFamiliesByChild(families);
    const slots: AncestorSlot[][] = [[root]];
    for (let depth = 1; depth < levels; depth += 1) {
        const next: AncestorSlot[] = [];
        slots[depth - 1].forEach((person) => {
            const family = person ? bestParentFamily(person, familiesByChild) : undefined;
            next.push(family?.father_handle ? peopleByHandle.get(family.father_handle) : undefined);
            next.push(family?.mother_handle ? peopleByHandle.get(family.mother_handle) : undefined);
        });
        slots.push(next);
    }
    return slots;
}

function ancestorDistances(root: RestPerson | undefined, people: RestPerson[], families: RestFamily[]): Map<string, number> {
    if (!root?.handle) return new Map();
    const peopleByHandle = new Map(people.filter((person) => person.handle).map((person) => [person.handle!, person]));
    const familiesByChild = indexFamiliesByChild(families);
    const distances = new Map<string, number>([[root.handle, 0]]);
    const queue = [root];
    while (queue.length) {
        const person = queue.shift()!;
        const family = bestParentFamily(person, familiesByChild);
        for (const handle of [family?.father_handle, family?.mother_handle]) {
            const parent = handle ? peopleByHandle.get(handle) : undefined;
            if (parent?.handle && !distances.has(parent.handle)) {
                distances.set(parent.handle, (distances.get(person.handle!) ?? 0) + 1);
                queue.push(parent);
            }
        }
    }
    return distances;
}

// OKLCH chroma for duplicate-ancestor colors. Kept low (well under the ~0.10
// "reads as gray" floor a fixed 8-slot categorical legend would need) because
// the brief asked for a pale/milky look here rather than saturated identity
// colors -- see the contrast target below for the trade-off this implies.
const DUPLICATE_COLOR_CHROMA = 0.06;
// WCAG AA text contrast against black is 4.5:1; this is deliberately pushed
// well past that minimum because a higher target forces a lighter (milkier)
// search result out of accessibleColorForHue below, which is the requested look.
const DUPLICATE_TEXT_CONTRAST_TARGET = 7;

function clampUnit(value: number): number {
    return Math.max(0, Math.min(1, value));
}

function linearToSrgbGamma(linearChannel: number): number {
    const clamped = clampUnit(linearChannel);
    return clamped <= 0.0031308 ? 12.92 * clamped : 1.055 * clamped ** (1 / 2.4) - 0.055;
}

function toHexByte(linearChannel: number): string {
    return Math.round(linearToSrgbGamma(linearChannel) * 255).toString(16).padStart(2, '0');
}

// Black's relative luminance is 0, so WCAG contrast against it simplifies to (L + 0.05) / 0.05.
function contrastAgainstBlack([r, g, b]: [number, number, number]): number {
    const luminance = 0.2126 * clampUnit(r) + 0.7152 * clampUnit(g) + 0.0722 * clampUnit(b);
    return (luminance + 0.05) / 0.05;
}

// OKLCH -> linear sRGB via OKLab (Björn Ottosson's reference matrices).
function oklchToLinearSrgb(lightness: number, chroma: number, hueDegrees: number): [number, number, number] {
    const hueRadians = (hueDegrees * Math.PI) / 180;
    const a = chroma * Math.cos(hueRadians);
    const b = chroma * Math.sin(hueRadians);
    const l_ = lightness + 0.3963377774 * a + 0.2158037573 * b;
    const m_ = lightness - 0.1055613458 * a - 0.0638541728 * b;
    const s_ = lightness - 0.0894841775 * a - 1.2914855480 * b;
    const l = l_ ** 3, m = m_ ** 3, s = s_ ** 3;
    return [
        +4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s,
        -1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s,
        -0.0041960863 * l - 0.7034186147 * m + 1.7076147010 * s,
    ];
}

// Raises OKLCH lightness at a fixed hue/chroma until the color's black-text
// contrast clears WCAG AA (4.5:1). A single fixed lightness can't guarantee
// this across the whole hue wheel: blues and purples read markedly darker
// than yellows or greens at the same perceptual lightness, so each hue is
// searched independently rather than sharing one lightness value.
function accessibleColorForHue(hueDegrees: number): string {
    let lightness = 0.5;
    let rgb = oklchToLinearSrgb(lightness, DUPLICATE_COLOR_CHROMA, hueDegrees);
    while (contrastAgainstBlack(rgb) < DUPLICATE_TEXT_CONTRAST_TARGET && lightness < 0.98) {
        lightness += 0.01;
        rgb = oklchToLinearSrgb(lightness, DUPLICATE_COLOR_CHROMA, hueDegrees);
    }
    return `#${rgb.map(toHexByte).join('')}`;
}

/**
 * Assigns one stable, black-text-readable color per person who appears more
 * than once across the ancestor slots -- i.e. reached through more than one
 * line of descent (pedigree collapse, typically from a cousin marriage
 * upstream). Colors are generated on demand rather than drawn from a fixed
 * palette: hues are spread evenly (360 / count) across exactly the number of
 * duplicated ancestors this render has, which -- unlike a fixed step such as
 * the golden angle -- is the max-min-optimal spacing for a *known* count. It
 * still can't guarantee every pair stays distinguishable under simulated
 * colorblindness once there are more than a handful of duplicates: the milky,
 * high-contrast requirement above compresses the usable lightness range so
 * much that hue is doing most of the identity work alone. Each duplicated
 * person's full name remains visible as text regardless, so identity is never
 * lost -- only the "spot the match at a glance by color" affordance degrades
 * for colorblind readers past a handful of duplicates.
 */
function assignDuplicateAncestorColors(slots: AncestorSlot[][]): Map<string, string> {
    const occurrences = new Map<string, number>();
    const firstSeenOrder: string[] = [];
    slots.forEach((ring) => ring.forEach((person) => {
        if (!person?.handle) return;
        const count = (occurrences.get(person.handle) ?? 0) + 1;
        occurrences.set(person.handle, count);
        if (count === 1) firstSeenOrder.push(person.handle);
    }));

    const duplicateHandles = firstSeenOrder.filter((handle) => (occurrences.get(handle) ?? 0) >= 2);
    const colors = new Map<string, string>();
    duplicateHandles.forEach((handle, index) => {
        colors.set(handle, accessibleColorForHue((index * 360) / duplicateHandles.length));
    });
    return colors;
}

function dedupePeople(...groups: Array<Array<RestPerson | undefined>>): RestPerson[] {
    const byHandle = new Map<string, RestPerson>();
    groups.flat().forEach((person) => { if (person?.handle) byHandle.set(person.handle, person); });
    return [...byHandle.values()];
}

/**
 * Maps each ancestor/family's own handle to their already-embedded events
 * (from `fetchAncestorPersons`/`fetchAncestorFamilies`'s `event_ref_list` extend)
 * -- no separate per-handle event fetch is needed to render arcs, tooltips, or
 * the name/occupation/year filters below. The migration-distance filter needs
 * each event's place coordinates too, which this embedded form doesn't carry;
 * `mainEventsByPersonWithPlace` below fills that in with a dedicated fetch.
 */
function eventsByOwner(people: RestPerson[], families: RestFamily[]): Map<string, RestEvent[]> {
    const result = new Map<string, RestEvent[]>();
    const add = (ownerHandle: string | undefined, event: RestEvent | undefined) => {
        if (!ownerHandle || !event?.handle) return;
        const ownedEvents = result.get(ownerHandle) ?? [];
        if (!ownedEvents.some((owned) => owned.handle === event.handle)) ownedEvents.push(event);
        result.set(ownerHandle, ownedEvents);
    };
    people.forEach((person) => (personExtended(person).events ?? []).forEach((event) => add(person.handle, event)));
    families.forEach((family) => (familyExtended(family).events ?? []).forEach((event) => {
        add(family.father_handle, event);
        add(family.mother_handle, event);
    }));
    return result;
}

function eventDate(person: RestPerson, type: string): string {
    return restEventDateString((personExtended(person).events ?? []).find((event) => event.type === type)?.date);
}

export function RadialAscendency() {
    const { mainId, sideItemId: selectedId, setMainId, setSideItemId, treeName } = useViewRoute();
    const token = useSyncExternalStore(subscribeToBearerToken, () => readBearerToken(treeName)) ?? undefined;
    const [level, setLevel] = useState(8);
    const [fontScale, setFontScale] = useState(1);
    const [filteredHandles, setFilteredHandles] = useState<Set<string>>(new Set());
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; personId: string } | null>(null);
    const container = useRef<HTMLDivElement>(null);
    const viewer = useRef<UncontrolledReactSVGPanZoom>(null);
    const size = useElementSize(container);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const ringWidth = useMemo(() => size?.width ? Math.floor(size.width * ASPECT_RATIO / level) : 0, [level, size?.width]);
    const arcs = useMemo(() => {
        if (!ringWidth) return undefined;
        return [...Array(level)].map((_, index) => arc<PieArcDatum<AncestorSlot>>().outerRadius((index + 1) * ringWidth).innerRadius(index * ringWidth));
    }, [level, ringWidth]);
    const fontSizes = useMemo(() => computeFontSizes(ringWidth, level, fontScale), [ringWidth, level, fontScale]);

    const rootQuery = useQuery({ queryKey: ['person', treeName, mainId], queryFn: () => fetchPersonByGrampsId(mainId!, token), enabled: !!mainId });
    const peopleQuery = useQuery({ queryKey: ['ancestors', 'people', treeName, mainId], queryFn: () => fetchAncestorPersons(mainId!, token), enabled: !!mainId });
    const familiesQuery = useQuery({ queryKey: ['ancestors', 'families', treeName, mainId], queryFn: () => fetchAncestorFamilies(mainId!, token), enabled: !!mainId });
    const selectedRootQuery = useQuery({ queryKey: ['person', treeName, selectedId], queryFn: () => fetchPersonByGrampsId(selectedId!, token), enabled: !!selectedId && selectedId !== mainId });
    const selectedPeopleQuery = useQuery({ queryKey: ['ancestors', 'people', treeName, selectedId], queryFn: () => fetchAncestorPersons(selectedId!, token), enabled: !!selectedId && selectedId !== mainId });
    const selectedFamiliesQuery = useQuery({ queryKey: ['ancestors', 'families', treeName, selectedId], queryFn: () => fetchAncestorFamilies(selectedId!, token), enabled: !!selectedId && selectedId !== mainId });

    const mainPeople = useMemo(() => dedupePeople(peopleQuery.data ?? [], [rootQuery.data]), [peopleQuery.data, rootQuery.data]);
    const mainEventsByPerson = useMemo(() => eventsByOwner(mainPeople, familiesQuery.data ?? []), [mainPeople, familiesQuery.data]);
    const mainEventHandles = useMemo(() => [...new Set([...mainEventsByPerson.values()].flat().flatMap((event) => event.handle ? [event.handle] : []))].sort(), [mainEventsByPerson]);
    const mainEventsWithPlaceQuery = useQuery({
        queryKey: ['events', treeName, 'ascendency-filter-places', handlesDigest(mainEventHandles)],
        queryFn: () => fetchEventsByHandles(mainEventHandles, token),
        enabled: mainEventHandles.length > 0,
    });
    // Merges place coordinates into the already-loaded events so the migration-distance
    // filter can work; falls back to the place-less event while this extra fetch is
    // still in flight so the name/occupation/year filters stay usable immediately.
    const mainEventsByPersonWithPlace = useMemo(() => {
        const withPlaceByHandle = new Map((mainEventsWithPlaceQuery.data ?? []).flatMap((event) => event.handle ? [[event.handle, event] as const] : []));
        if (withPlaceByHandle.size === 0) return mainEventsByPerson;
        const result = new Map<string, RestEvent[]>();
        mainEventsByPerson.forEach((events, owner) => result.set(owner, events.map((event) => (event.handle && withPlaceByHandle.get(event.handle)) || event)));
        return result;
    }, [mainEventsByPerson, mainEventsWithPlaceQuery.data]);
    const slots = useMemo(() => buildSlots(rootQuery.data, mainPeople, familiesQuery.data ?? [], level), [rootQuery.data, mainPeople, familiesQuery.data, level]);
    const duplicateAncestorColors = useMemo(() => assignDuplicateAncestorColors(slots), [slots]);
    const selectedRoot = selectedId === mainId ? rootQuery.data : selectedRootQuery.data;
    const selectedPeople = useMemo(() => selectedId === mainId ? mainPeople : dedupePeople(selectedPeopleQuery.data ?? [], [selectedRoot]), [mainId, mainPeople, selectedId, selectedPeopleQuery.data, selectedRoot]);
    const closestCommonAncestorHandle = useMemo(() => {
        if (!rootQuery.data?.handle || !selectedRoot?.handle || rootQuery.data.handle === selectedRoot.handle) return null;
        const mainDistances = ancestorDistances(rootQuery.data, mainPeople, familiesQuery.data ?? []);
        if (mainDistances.has(selectedRoot.handle)) return null;
        const selectedDistances = ancestorDistances(selectedRoot, selectedPeople, selectedId === mainId ? familiesQuery.data ?? [] : selectedFamiliesQuery.data ?? []);
        let closest: string | null = null;
        let bestScore = Infinity;
        let bestSelectedDistance = Infinity;
        selectedDistances.forEach((selectedDistance, handle) => {
            const mainDistance = mainDistances.get(handle);
            if (mainDistance === undefined) return;
            const score = mainDistance + selectedDistance;
            if (score < bestScore || (score === bestScore && selectedDistance < bestSelectedDistance)) {
                closest = handle;
                bestScore = score;
                bestSelectedDistance = selectedDistance;
            }
        });
        return closest;
    }, [familiesQuery.data, mainId, mainPeople, rootQuery.data, selectedFamiliesQuery.data, selectedId, selectedPeople, selectedRoot]);

    const handleClick = useCallback((event: React.MouseEvent<SVGGElement>) => {
        const sideItemId = event.currentTarget.dataset.itemId;
        if (sideItemId) setSideItemId(sideItemId);
    }, [setSideItemId]);

    const handleContextMenu = useCallback((event: React.MouseEvent<SVGGElement>) => {
        const personId = event.currentTarget.dataset.itemId;
        if (!personId) return;
        event.preventDefault();
        setContextMenu({ mouseX: event.clientX, mouseY: event.clientY, personId });
    }, []);

    // Forces a fresh `UncontrolledReactSVGPanZoom` instance (not just a prop update)
    // whenever the geometry it was built for changes: the library keeps its pan/zoom
    // state as `viewerWidth`/`SVGWidth` fields patched independently of the scale/pan
    // matrix on ordinary prop updates, so an update-in-place can leave the matrix
    // computed for a since-outdated size. A remount sidesteps that entirely — the
    // instance's very first state is always built from the size/level current at
    // the moment it is constructed.
    const viewerKey = `${size?.width ?? 0}x${size?.height ?? 0}x${level}`;

    // useLayoutEffect (not useEffect+rAF) runs synchronously right after the DOM
    // commit, before the browser paints and before anything async (a ResizeObserver
    // notification, a query resolving) can slip in another render first. Combined
    // with the remount above, this call is always acting on the instance that was
    // just built for the exact geometry currently on screen — no stale/mismatched
    // state, no dependency-array guesswork about what triggered the mount.
    useLayoutEffect(() => {
        viewer.current?.fitToViewer();
    }, [viewerKey]);

    if (!mainId) return null;
    const isLoading = rootQuery.isLoading || peopleQuery.isLoading || familiesQuery.isLoading;
    const hasError = !!rootQuery.error || !!peopleQuery.error || !!familiesQuery.error;
    const ancestorPie = pie<AncestorSlot>().value(() => 1).startAngle(-Math.PI / 2).endAngle(Math.PI / 2).sort(null);
    const halfWidth = size?.width ? size.width / 2 : undefined;
    const halfHeight = size?.height ? size.height / 2 : undefined;
    const wheelRadius = ringWidth * level / 2;

    return <Box ref={container} sx={{ width: '100%', height: isMobile ? '40vh' : '75vh' }}>
        <Accordion disableGutters sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="filter-panel-content" id="filter-panel-header"><Stack direction="row" spacing={1} useFlexGap sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}><Typography>Filtres</Typography><RestPersonFilterActiveSummary /></Stack></AccordionSummary>
            <AccordionDetails><Typography>Affichage de {level} générations <small>- utilisez le curseur pour ajuster le nombre de générations affichées.</small></Typography><Slider size="small" value={level} aria-label="Nombre de générations" valueLabelDisplay="auto" min={3} max={12} onChange={(event, value) => { void event; setLevel(value as number); }} /><Typography>Taille du texte : {Math.round(fontScale * 100)}% <small>- la taille est calculée automatiquement pour éviter les chevauchements ; ajustez si besoin.</small></Typography><Slider size="small" value={fontScale} aria-label="Taille du texte" valueLabelDisplay="auto" valueLabelFormat={(value) => `${Math.round(value * 100)}%`} min={0.5} max={2} step={0.1} onChange={(event, value) => { void event; setFontScale(value as number); }} /><RestPersonFilter persons={mainPeople} eventsByPerson={mainEventsByPersonWithPlace} onFilterChange={setFilteredHandles} /></AccordionDetails>
        </Accordion>
        {hasError && <Alert severity="error">Impossible de charger les ascendants REST.</Alert>}
        {isLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
        {!isLoading && !rootQuery.data && <Alert severity="warning">La personne demandée est introuvable.</Alert>}
        {halfWidth && halfHeight && arcs?.[level - 1] && rootQuery.data && <UncontrolledReactSVGPanZoom key={viewerKey} background="white" ref={viewer} width={size!.width} height={size!.height} detectAutoPan={false} detectWheel scaleFactor={1.5}>
            <svg viewBox={`${-halfWidth} ${-halfHeight} ${2 * halfWidth} ${2 * halfHeight}`} style={{ maxWidth: '100%', height: 'auto' }}><g transform={`translate(0 ${wheelRadius})`}>{slots.map((datum, depth) => {
                const datumPie = ancestorPie(datum);
                return datum.map((person, index) => {
                    const angle = (datumPie[index].startAngle + datumPie[index].endAngle) / 2;
                    const degrees = angle * 180 / Math.PI;
                    const rotate = degrees > 0 ? degrees - 90 : degrees + 90;
                    const name = person ? restPersonName(person) : '';
                    const [firstName = '', ...surname] = name.split(' ');
                    // A duplicated ancestor (reached through more than one line of
                    // descent) always renders at full opacity in its assigned color,
                    // overriding the normal per-depth fade -- otherwise the same
                    // person could look like two different colors depending on which
                    // ring each occurrence happens to land on.
                    const duplicateColor = person?.handle ? duplicateAncestorColors.get(person.handle) : undefined;
                    return <g className={`arc arc${depth} ${person ? 'has-person' : ''} ${person?.handle && filteredHandles.has(person.handle) ? 'filtered' : ''} ${person?.gramps_id === selectedId ? 'selected' : ''} ${person?.handle === closestCommonAncestorHandle ? 'closest-common-ancestor' : ''}`} fill={duplicateColor ?? schemeCategory10[index % 2]} key={`${depth}-${index}`} data-item-id={person?.gramps_id} fillOpacity={duplicateColor ? 1 : 1 / (depth + 1)} onClick={handleClick} onContextMenu={handleContextMenu}>
                        {person && <title>{name} ({eventDate(person, 'Birth')} - {eventDate(person, 'Death')})</title>}<path d={arcs[depth](datumPie[index])!} /><text textAnchor="middle" transform={`translate(${arcs[depth].centroid(datumPie[index])}) ${depth > 4 ? `rotate(${rotate})` : ''}`} style={{ fontSize: fontSizes[depth] }} className="svg-person">{person && (depth < 8 ? <><tspan x="0" dy="0em">{truncate(firstName)}</tspan><tspan x="0" dy="0.9em">{truncate(surname.join(' '))}</tspan></> : <tspan x="0" dy="0em">{truncate(name)}</tspan>)}</text>
                    </g>;
                });
            })}</g></svg>
        </UncontrolledReactSVGPanZoom>}
        {contextMenu && <PersonContextMenu
            anchorPosition={{ mouseX: contextMenu.mouseX, mouseY: contextMenu.mouseY }}
            personGrampsId={contextMenu.personId}
            treeName={treeName}
            token={token}
            onClose={() => setContextMenu(null)}
            onCenter={setMainId}
            onGoTo={setSideItemId}
        />}
    </Box>;
}

function truncate(input: string) {
    return input.length > 16 ? `${input.substring(0, 16)}...` : input;

}
